import React, { useState, useEffect, useRef } from "react";
import { CSVLink } from "react-csv";
import { connect } from "react-redux";

import {
  emptyString,
  loginConstants,
  groupPerformanceStoresName,
  groupPerformanceExportHeaders,
  performanceScreensConstants,
} from "../../constants";
import {
  AVERAGE_RATING_RESPONSE_TYPE,
  starRatingGuestSentimentResponse,
  GET_RESPONSE_TYPE,
} from "../../../admin-screens/constants";
import {
  PERFORMANCE_DEFAULT_SORTING,
  PERFORMANCE_FILTER_DEFAULT_MIN_STAR,
  PERFORMANCE_FILTER_INITIAL_MAX_STAR,
  PERFORMANCE_EXPORT_CSV_DELAY_MS,
  modalSize,
} from "../../../../constants";
import { getItemFromStorage } from "../../../../system";
import {
  isInclude,
  fileName,
  calculateChange,
  escapeSearchQuery,
  convertToDateToUnix,
  updatedColumnHeaders,
  convertFromDateToUnix,
  datePreferenceUtility,
  useRatingCheckboxGroup,
  guestSentimentPaginatedPostRequestBody,
  getConfirmationModal,
  UTCToDateFormat,
  timestampToDateTimeFormat,
  handleAsyncAction,
} from "../../../../shared";
import { groupPerformanceActions } from "../../redux/actions";
import {
  brandPerformanceActions,
  individualStoreProfileActions,
} from "../../../shared-screens/redux/actions";
import {
  buildSurveyCompletionDateFilterQuery,
  buildDatedPerformanceRequestPayload,
  mapStoresPerformanceExportRecords,
} from "../../../shared-screens/views/shared/performanceQueryUtility";
import Performance from "./performance";
import FilterComponent from "./filterComponent";
import useModalDisplay from "../../../../shared/components/hooks/useModalDisplay/useModalDisplay";

const { GUEST_SENTIMENTS, GUEST_TRANSACTION } = performanceScreensConstants;

const GroupPerformance = (props) => {
  const {
    groupPerformanceReducer: {
      allItemsForExport,
      franchiseResponsesData,
      allFranchiseResponsesData,
      groupResponseDataPagination,
      groupsAvgSentimentAndTransactionRatingData,
    },
    loggedUser,
    frRoute,
    resetAllExportItems,
    getRecordPaginationResponses,
    getAverageRatingResponses,
    getAllFranchiseResponsesItemsForExport,
    exportDetailCustomerSurveyData,
  } = props;

  const csvLinkRef = useRef();
  const [exportData, setExportData] = useState([]);
  const [exportHeaders, setExportHeaders] = useState([]);
  const [sorting, setSorting] = useState(PERFORMANCE_DEFAULT_SORTING);
  const [selectedOption, setSelectedOption] = useState([]);
  const [maxStar, setMaxStar] = useState(PERFORMANCE_FILTER_INITIAL_MAX_STAR);
  const [minStar, setMinStar] = useState(PERFORMANCE_FILTER_DEFAULT_MIN_STAR);
  const [starValue, setStarValue] = useState([]);
  const [dayCounts, setDayCounts] = useState(emptyString);
  const [queryString, setQueryString] = useState(emptyString);
  const [searchString, setSearchString] = useState(emptyString);
  const [dateState, setDateState] = useState(datePreferenceUtility());

  const [showModal, modalElement, setComponent, closeModal] = useModalDisplay();

  const brandId = loggedUser.brandId;

  const createDateFrom = convertFromDateToUnix(dateState.startDate);
  const createDateTo = convertToDateToUnix(dateState.endDate);

  const { handleRatingOptionChange, selectedRatingOption } =
    useRatingCheckboxGroup(selectedOption, setStarValue, setSelectedOption);

  // set pagination details with request body
  const getPaginatedPostRequestBody = (pageNumber, sortBy, size) => {
    const averageSentiments = {
      min: minStar,
      max: maxStar,
    };
    const requestBody = guestSentimentPaginatedPostRequestBody({
      pagination: groupResponseDataPagination,
      pageNumber: pageNumber,
      sortBy: sortBy || sorting,
      filterQuery: queryString,
      averageSentiments: averageSentiments,
      isFrRoute: frRoute,
      size: size,
      isGuestSentimentFilter: selectedRatingOption === GUEST_SENTIMENTS,
    });
    if (sortBy) {
      setSorting(sortBy);
    }
    return { ...requestBody, search: escapeSearchQuery(searchString) };
  };

  // get table data with pagination
  const getData = (pageNumber, sortBy, size) => {
    const requestBody = getPaginatedPostRequestBody(pageNumber, sortBy, size);
    const groupId = getItemFromStorage(loginConstants.GROUP_ID);

    //If the selected checkbox option matches the sentiments or transaction filter query in the payload,
    //or if neither sentiments nor transaction filter is present, proceed with data retrieval.
    if (
      (isInclude(requestBody.filter, GUEST_SENTIMENTS) &&
        selectedRatingOption === GUEST_SENTIMENTS) ||
      (isInclude(requestBody.filter, GUEST_TRANSACTION) &&
        selectedRatingOption === GUEST_TRANSACTION) ||
      !(
        isInclude(requestBody.filter, GUEST_TRANSACTION) ||
        isInclude(requestBody.filter, GUEST_SENTIMENTS)
      )
    ) {
      const { GET_GROUP_USER_RESPONSES } = GET_RESPONSE_TYPE;

      getRecordPaginationResponses(
        buildDatedPerformanceRequestPayload({
          createDateFrom,
          createDateTo,
          id: groupId,
          type: GET_GROUP_USER_RESPONSES,
          requestBody,
        }),
      );
      const { GET_GROUPS_AVERAGE_RATING_RESPONSES } =
        AVERAGE_RATING_RESPONSE_TYPE;

      getAverageRatingResponses(
        buildDatedPerformanceRequestPayload({
          createDateFrom,
          createDateTo,
          id: groupId,
          type: GET_GROUPS_AVERAGE_RATING_RESPONSES,
          requestBody,
        }),
      );
    }
  };

  useEffect(() => {
    props.resetRestaurantProfile();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (queryString || searchString) {
      getData(0, null, groupResponseDataPagination.size);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchString, queryString, selectedRatingOption]);

  useEffect(() => {
    setQueryString(
      buildSurveyCompletionDateFilterQuery({
        createDateFrom,
        createDateTo,
        starValue,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption, createDateTo, createDateFrom, starValue]);

  //Used to export CSV
  const exportCSV = () => {
    setTimeout(() => {
      csvLinkRef.current?.link.click();
    }, PERFORMANCE_EXPORT_CSV_DELAY_MS);
  };

  //Get all data for export
  const getExportData = () => {
    let headers;
    let requestId;
    let requestBody;
    const averageSentiments = {
      min: minStar,
      max: maxStar,
    };
    //Define common dataToSend array
    const dataToSend = {
      pagination: {
        size: groupResponseDataPagination.size,
      },
      pageNumber: groupResponseDataPagination.number,
      sortBy: sorting,
      filterQuery: queryString,
      averageSentiments,
      isFrRoute: frRoute,
      size: groupResponseDataPagination.size,
      isGuestSentimentFilter: selectedRatingOption === GUEST_SENTIMENTS,
    };

    //Determine requestId based on the type
    headers = groupPerformanceExportHeaders;
    requestId = getItemFromStorage(loginConstants.GROUP_ID);

    //Create the requestBody object
    requestBody = guestSentimentPaginatedPostRequestBody(dataToSend);
    requestBody.search = searchString;

    //Determine the appropriate export function based on type
    getAllFranchiseResponsesItemsForExport(
      createDateFrom,
      createDateTo,
      requestId,
      requestBody,
    );

    setExportHeaders(updatedColumnHeaders(headers, dayCounts));
  };

  const handleConfirmCustomerSurveyData = () => {
    handleAsyncAction(
      exportDetailCustomerSurveyData(
        timestampToDateTimeFormat(createDateFrom),
        timestampToDateTimeFormat(createDateTo),
        brandId,
      ),
      { onSuccess: closeModal },
    );
  };

  // Open confirmationModal for export customer survey data
  const openExportCustomerDataConfirmationModal = () => {
    setComponent(
      getConfirmationModal({
        ModalSize: modalSize.MODAL_SIZE_MEDIUM,
        Title: "Confirmation",
        onClose: closeModal,
        modalBodyCssClassName: "text-center",
        selectedDateRange: `${UTCToDateFormat(
          createDateFrom,
        )} - ${UTCToDateFormat(createDateTo)}`,
        onConfirm: handleConfirmCustomerSurveyData,
        message:
          "Please confirm that you would like to export survey data for the following dates:",
        selectedDateRangeMessage:
          "Date ranges can be adjusted using the Selected Date parameters on the performance page",
      }),
    );
  };

  useEffect(() => {
    if (allItemsForExport.length) {
      setExportData(
        mapStoresPerformanceExportRecords(allItemsForExport, calculateChange),
      );
      exportCSV();
      resetAllExportItems();
    }
    // eslint-disable-next-line
  }, [allItemsForExport]);

  return (
    <>
      {showModal && modalElement}
      <FilterComponent
        dateState={dateState}
        setMaxStar={setMaxStar}
        setMinStar={setMinStar}
        setStarValue={setStarValue}
        setDateState={setDateState}
        setDayCounts={setDayCounts}
        setSelectedOption={setSelectedOption}
        selectedRatingOption={selectedRatingOption}
        handleRatingOptionChange={handleRatingOptionChange}
      />
      <Performance
        getData={getData}
        sorting={sorting}
        dayCounts={dayCounts}
        searchString={searchString}
        selectedOption={selectedOption}
        setSearchString={setSearchString}
        exportFunc={() => getExportData()}
        userResponsesData={franchiseResponsesData}
        avgSentimentAndTransactionRatingData={
          groupsAvgSentimentAndTransactionRatingData
        }
        totalRestaurants={allFranchiseResponsesData.totalItems}
        totalResponses={allFranchiseResponsesData.totalResponses}
        userResponseDataPagination={groupResponseDataPagination}
        starRatingGuestSentimentResponse={starRatingGuestSentimentResponse}
        openExportCustomerDataConfirmationModal={
          openExportCustomerDataConfirmationModal
        }
      />
      <CSVLink
        target="_blank"
        ref={csvLinkRef}
        className="hidden"
        data={exportData}
        headers={exportHeaders}
        filename={`${fileName(
          groupPerformanceStoresName,
          createDateFrom,
          createDateTo,
        )}.csv`}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  groupPerformanceReducer: state.groupPerformanceReducer,
  loggedUser: state.authentication.user,
});

const mapDispatchToProps = (dispatch) => ({
  getRecordPaginationResponses: (payload) =>
    dispatch(brandPerformanceActions.getRecordPaginationResponses(payload)),
  getAllFranchiseResponsesItemsForExport: (
    createDateFrom,
    createDateTo,
    groupId,
    body,
  ) =>
    dispatch(
      groupPerformanceActions.getAllFranchiseResponsesItemsForExport(
        createDateFrom,
        createDateTo,
        groupId,
        body,
      ),
    ),
  resetRestaurantProfile: () =>
    dispatch(individualStoreProfileActions.resetRestaurantProfile()),
  resetAllExportItems: () =>
    dispatch(groupPerformanceActions.resetAllExportItems()),
  getAverageRatingResponses: (payload) =>
    dispatch(brandPerformanceActions.getAverageRatingResponses(payload)),
  exportDetailCustomerSurveyData: (createDateFrom, createDateTo, brandId) =>
    dispatch(
      brandPerformanceActions.exportDetailCustomerSurveyData(
        createDateFrom,
        createDateTo,
        brandId,
      ),
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(GroupPerformance);
